import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {addOrReplaceAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {ADD_ICON_WITH_HOVER_STYLES} from 'analysis-tool/AnalysisToolConstants';
import {isAnalysisToolRedesignEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {isViewTerminalStepType} from 'analysis-tool/data/LineageStepTypes';
import {recordAnalysisAction} from 'analysis-tool/ui/debugAnalysisActionHistory';
import {assert} from 'Asserts';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import FilterEditorModal from 'data-frames/filters/components/FilterEditorModal';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import InputAwareStepConfigEditorModal from 'pipelines/generalized-step-config-editor/InputAwareStepConfigEditorModal';
import {isStepInputParentsComplete} from 'pipelines/generalized-step-config-editor/StepInputParentsConfiguration';
import {AddOrReplaceAndRunStepParams, DraftStepInputParents} from 'pipelines/network/PipelineDAO';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import Tooltip from 'ui-library/bs/Tooltip';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import AddIcon from 'ui-library/icons/AddIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';

import {ADDABLE_TRANSFORMATION_STEPS} from './ChartViewStepEditorConstants';

export const FIX_FAILED_TRANSFORMS_MESSAGE =
  'Fix all failed transformations before adding more transformations';
export const WAITING_FOR_STEP_SAVE_MESSAGE = 'Waiting for step to save';
export const BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE =
  'Cannot add transformations when there is a manual flagging downstream';

/** @package */
export interface AddTransformStepDropdownProps {
  viewSteps: Array<GetAnalysisPipelineStep>;
  dataFrameSpec: DataFrameSpec | null;
  inputDataFrameId: string;
  analysis: AnalysisGraphQL;
  canAddNewSteps: boolean;
  addNewStepsDisabledMessage?: string;
}

/** @package */
export function getStepOutputChildren(
  viewSteps: Array<GetAnalysisPipelineStep>
): Record<string, Array<{stepId: string; stepDataName: string}>> | undefined {
  const lastStep = viewSteps[viewSteps.length - 1];
  if (isViewTerminalStepType(lastStep.definition.stepType)) {
    return {
      [SINGLE_DATASET_OUTPUT_STEP_DATA_NAME]: [
        {
          stepId: lastStep.id,
          stepDataName: SINGLE_DATASET_INPUT_STEP_DATA_NAME,
        },
      ],
    };
  } else if (lastStep.definition.outgoingEdges.length > 0) {
    return {
      [SINGLE_DATASET_OUTPUT_STEP_DATA_NAME]: lastStep.definition.outgoingEdges.map((edge) => ({
        stepId: edge.child.step.id,
        stepDataName: edge.childStepData.name,
      })),
    };
  }
  return undefined;
}

export default function AddTransformStepDropdown({
  viewSteps,
  dataFrameSpec,
  inputDataFrameId,
  analysis,
  canAddNewSteps,
  addNewStepsDisabledMessage = FIX_FAILED_TRANSFORMS_MESSAGE,
}: AddTransformStepDropdownProps): JSX.Element {
  const lastNonTerminalStep = viewSteps.findLast((step) => !isViewTerminalStepType(step.definition.stepType));
  assert(lastNonTerminalStep != null, 'Expected viewSteps to contain a non-terminal step');

  const stepOutputChildren = getStepOutputChildren(viewSteps);

  const useConsolidatedFilter = isAnalysisToolRedesignEnabled();

  const allAddableTransformSteps = useConsolidatedFilter
    ? [
        [{stepType: PipelineStepType.FILTER_DATASET as const, stepVersion: 0}],
        ...ADDABLE_TRANSFORMATION_STEPS,
      ]
    : ADDABLE_TRANSFORMATION_STEPS;

  const sectionedOptions = [];

  for (const section of allAddableTransformSteps) {
    const options = section.map(({stepType, stepVersion}) => ({
      key: stepType,
      text: STEP_TYPE_TO_USER_FACING_NAME[stepType],
      onSelect: () => {
        if (!dataFrameSpec) {
          return;
        }

        runModal(InputAwareStepConfigEditorModal, {
          stepType,
          stepVersion,
          inputDataFrameSpec: dataFrameSpec,
          analysisId: analysis.id,
          precedingStep: lastNonTerminalStep,
          clientStateId: lastNonTerminalStep.id,
          step: null,
          handleRequestSubmit: (
            config: PipelineStepConfig<PipelineStepType, number>,
            stepInputParents: DraftStepInputParents | null
          ) => {
            assert(
              stepInputParents == null || isStepInputParentsComplete(stepInputParents, stepType, stepVersion),
              'Expected step input parents information to be complete if defined'
            );
            recordAnalysisAction('ADD_TRANSFORM', {
              analysisId: analysis.id,
              stepType,
              parentStepType: lastNonTerminalStep.definition.stepType,
            });
            return withLoadingScreen(
              addOrReplaceAndRunAnalysisStep(analysis.id, {
                stepType,
                stepVersion,
                stepConfig: config,
                stepInput: {inputDataFrameId},
                stepOutputChildren,
                ...(stepInputParents == null ? {} : {stepInputParents}),
              } as unknown as AddOrReplaceAndRunStepParams),
              'Updating transformations...'
            );
          },
        });
      },
    }));
    options.sort((a, b) => a.text.localeCompare(b.text));
    sectionedOptions.push(options);
  }

  return (
    <Tooltip disabled={canAddNewSteps} title={addNewStepsDisabledMessage}>
      <Dropdown
        menu={
          <Menu dropLeft={true} width={MenuWidth.MEDIUM} height={MenuHeight.MEDIUM}>
            {!useConsolidatedFilter && (
              <Section>
                <Option
                  onSelect={() => {
                    runModal(FilterEditorModal, {
                      analysis,
                      viewSteps,
                      canSelectColumn: true,
                      canAddNewFilters: canAddNewSteps,
                    });
                  }}
                  dataTestAttrs={{
                    component: 'AddTransformStepDropdown',
                    element: 'option',
                    key: 'FILTER_DATASET',
                  }}
                >
                  Filter dataset
                </Option>
              </Section>
            )}
            {sectionedOptions.map((options, i) => (
              <Section key={i}>
                {options.map(({key, onSelect, text}) => (
                  <Option
                    key={key}
                    onSelect={onSelect}
                    dataTestAttrs={{component: 'AddTransformStepDropdown', element: 'option', key}}
                  >
                    {text}
                  </Option>
                ))}
              </Section>
            ))}
          </Menu>
        }
      >
        <AddIcon
          disabled={!canAddNewSteps || !dataFrameSpec}
          className={css(styles.addIcon)}
          dataTestAttrs={{component: 'AddTransformStepDropdown', element: 'addButton'}}
        />
      </Dropdown>
    </Tooltip>
  );
}

/** @package */
export const styles = StyleSheet.create({
  addIcon: ADD_ICON_WITH_HOVER_STYLES,
});
